import { Eraser } from "lucide-react";
import SignaturePadLib from "signature_pad";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface SignaturePadHandle {
  /** True when nothing has been drawn. */
  isEmpty: () => boolean;
  /** PNG data URL of the drawing (transparent background). */
  toDataURL: () => string;
  /** Erase the pad. */
  clear: () => void;
}

interface SignaturePadProps {
  /** Fired after each stroke and on clear, so the parent can enable/disable save. */
  onChange?: () => void;
  className?: string;
}

/**
 * Canvas-based signature pad (wraps the framework-agnostic `signature_pad`
 * package). Draws on a transparent background so the exported PNG overlays
 * cleanly on letters/forms. Handles device-pixel-ratio scaling for crisp lines
 * and preserves the drawing across container resizes. Read the result through
 * the imperative handle: `isEmpty()` / `toDataURL()` / `clear()`.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ onChange, className }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const padRef = useRef<SignaturePadLib | null>(null);
    // Keep the latest onChange without re-initializing the pad each render.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const pad = new SignaturePadLib(canvas, {
        backgroundColor: "rgba(0,0,0,0)",
        penColor: "#111827",
      });
      padRef.current = pad;

      const handleEnd = () => onChangeRef.current?.();
      pad.addEventListener("endStroke", handleEnd);

      const resize = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const data = pad.toData(); // preserve the drawing across the resize
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext("2d")?.scale(ratio, ratio);
        pad.clear();
        pad.fromData(data);
      };
      resize();
      window.addEventListener("resize", resize);

      return () => {
        window.removeEventListener("resize", resize);
        pad.removeEventListener("endStroke", handleEnd);
        pad.off(); // detach the pad's pointer listeners
        padRef.current = null;
      };
    }, []);

    useImperativeHandle(ref, () => ({
      isEmpty: () => padRef.current?.isEmpty() ?? true,
      toDataURL: () => padRef.current?.toDataURL("image/png") ?? "",
      clear: () => {
        padRef.current?.clear();
        onChangeRef.current?.();
      },
    }), []);

    return (
      <div className={cn("grid gap-2", className)}>
        <canvas
          ref={canvasRef}
          aria-label="Signature drawing area"
          className="bg-background h-40 w-full touch-none rounded-lg border"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              padRef.current?.clear();
              onChangeRef.current?.();
            }}
          >
            <Eraser className="size-4" /> Clear
          </Button>
        </div>
      </div>
    );
  },
);
